import { useState } from 'react';
import { ChevronDown, ChevronRight, EyeOff } from 'lucide-react';

const REASON_LABELS = {
  discipline_mismatch: 'Discipline mismatch',
  'unresolvable pset_name': 'Unresolvable property set',
};

function reasonDetail(rule) {
  const a = rule.applicability || {};
  if (rule.skip_reason === 'discipline_mismatch' && (a.rule_discipline || a.file_discipline)) {
    return `rule targets ${a.rule_discipline || '—'} · file is ${a.file_discipline || '—'}`;
  }
  if (a.pset_name) return `Pset: ${a.pset_name}`;
  return null;
}

/**
 * P02 Batch 5 — "Rules not run" transparency panel.
 * Groups audit.summary.skipped_by_applicability by skip_reason so
 * reviewers see WHY compiled rules never executed. Renders nothing
 * when the audit carries no skips (fail-open for legacy/WZB audits).
 */
export const RulesNotRunPanel = ({ summary }) => {
  const [open, setOpen] = useState(false);
  const skipped = summary?.skipped_by_applicability;
  if (!Array.isArray(skipped) || skipped.length === 0) return null;

  const stats = summary?.applicability_partition_stats || {};
  const byReason = {};
  for (const r of skipped) {
    const key = r.skip_reason || 'unspecified';
    if (!byReason[key]) byReason[key] = [];
    byReason[key].push(r);
  }
  const groups = Object.entries(byReason).sort((a, b) => b[1].length - a[1].length);
  const slug = (s) => String(s).replace(/\s+/g, '-');

  return (
    <div className="card-elevated p-4 mb-6" data-testid="rules-not-run-panel">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 text-left"
        data-testid="rules-not-run-toggle"
      >
        {open
          ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
        <EyeOff className="h-4 w-4 text-muted-foreground shrink-0" />
        <h3 className="text-sm font-semibold text-foreground">Rules not run</h3>
        <span className="text-xs text-muted-foreground" data-testid="rules-not-run-count">
          {skipped.length} of {stats.total != null ? stats.total : skipped.length} compiled
          rule{skipped.length !== 1 ? 's' : ''} did not execute on this audit
        </span>
        <span className="ml-auto flex items-center gap-1.5 flex-wrap justify-end">
          {groups.map(([reason, rules]) => (
            <span
              key={reason}
              className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground whitespace-nowrap"
              data-testid={`rules-not-run-badge-${slug(reason)}`}
            >
              {(REASON_LABELS[reason] || reason)} · {rules.length}
            </span>
          ))}
        </span>
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          {groups.map(([reason, rules]) => (
            <div key={reason} data-testid={`rules-not-run-group-${slug(reason)}`}>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                {REASON_LABELS[reason] || reason} — {rules.length}
              </p>
              <div className="space-y-1">
                {rules.map((r, i) => {
                  const detail = reasonDetail(r);
                  return (
                    <div
                      key={`${r.rule_id}-${i}`}
                      className="flex items-baseline gap-2 text-xs py-1 px-2 rounded bg-muted/40"
                      data-testid={`rules-not-run-rule-${r.rule_id}`}
                    >
                      <span className="font-medium text-foreground">{r.rule_name || r.rule_id}</span>
                      {r.category ? <span className="text-muted-foreground">{r.category}</span> : null}
                      {r.source_page > 0 ? <span className="text-muted-foreground">BEP p.{r.source_page}</span> : null}
                      {detail ? <span className="text-muted-foreground ml-auto text-right">{detail}</span> : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
